/**
 * Scanner module - Barcode scanning functionality
 */
import { getSettings } from '../services/storage.js';
import { triggerScanEffects } from '../helpers/triggerScanEffects.js';
import { resizeScanFrame } from '../helpers/resizeScanFrame.js';
import { toggleTorchButtonStatus } from '../helpers/toggleTorchButtonStatus.js';
import { toastify } from '../helpers/toastify.js';
import { log } from '../utils/log.js';
import {
  shouldContinueScanning,
  startScanning,
  stopScanning,
  getBarcodeReader,
  setScanTimeoutId,
  getScanTimeoutId
} from '../state.js';
import { SCAN_RATE_LIMIT } from '../constants.js';
import { fetchProductData } from './product.js';

/**
 * DOM element references
 */
let videoCaptureEl = null;
let videoCaptureVideoEl = null;
let videoCaptureActionsEl = null;
let scanBtn = null;
let scanFrameEl = null;
let scanInstructionsEl = null;
let cameraPanel = null;
let dropzoneEl = null;
let bsHistoryEl = null;
let cameraSelect = null;
let torchButton = null;

/**
 * Initializes the scanner module with DOM references
 * @param {object} elements - DOM element references
 */
export function initScanner(elements) {
  videoCaptureEl = elements.videoCaptureEl;
  videoCaptureVideoEl = elements.videoCaptureVideoEl;
  videoCaptureActionsEl = elements.videoCaptureActionsEl;
  scanBtn = elements.scanBtn;
  scanFrameEl = elements.scanFrameEl;
  scanInstructionsEl = elements.scanInstructionsEl;
  cameraPanel = elements.cameraPanel;
  dropzoneEl = elements.dropzoneEl;
  bsHistoryEl = elements.bsHistoryEl;
  cameraSelect = elements.cameraSelect;
  torchButton = elements.torchButton;
}

/**
 * Extracts barcode value from detection result
 * @param {object} barcode - Barcode detection result
 * @returns {string} Barcode value or empty string
 */
function extractBarcodeValue(barcode) {
  if (!barcode || typeof barcode !== 'object') {
    return '';
  }

  let value = barcode.rawValue || barcode.text || barcode.data || '';

  if (typeof value !== 'string') {
    value = String(value || '');
  }

  return value;
}

/**
 * Main scan function - detects barcodes from video stream
 */
export async function scan() {
  if (!shouldContinueScanning()) {
    return;
  }

  log.info('Scanning...');
  scanInstructionsEl?.removeAttribute('hidden');

  try {
    const [, settings] = await getSettings();
    const barcodeReader = getBarcodeReader();

    // Get video element from shadow DOM dynamically
    const videoEl = videoCaptureEl?.shadowRoot?.querySelector('video');

    if (!barcodeReader || !videoEl) {
      throw new Error('Scanner not initialized');
    }

    const barcode = await barcodeReader.detect(videoEl);
    const barcodeValue = extractBarcodeValue(barcode);

    if (!barcodeValue) {
      throw new Error('No barcode detected');
    }

    log.info(`Barcode detected: ${barcodeValue}`);
    await fetchProductData(barcodeValue);

    if (settings?.addToHistory) {
      bsHistoryEl?.add(barcodeValue);
    }

    triggerScanEffects();

    // Stop scanning if continuous mode is disabled
    if (!settings?.continueScanning) {
      const timeoutId = getScanTimeoutId();
      if (timeoutId) {
        clearTimeout(timeoutId);
        setScanTimeoutId(null);
      }
      scanBtn?.removeAttribute('hidden');
      scanFrameEl?.setAttribute('hidden', '');
      videoCaptureActionsEl?.setAttribute('hidden', '');
      return;
    }
  } catch {
    // No barcode detected - continue scanning silently
  }

  if (shouldContinueScanning()) {
    setScanTimeoutId(setTimeout(() => scan(), SCAN_RATE_LIMIT));
  }
}

/**
 * Handles scan button click
 */
export function handleScanButtonClick() {
  scanBtn?.setAttribute('hidden', '');
  scanFrameEl?.removeAttribute('hidden');
  videoCaptureActionsEl?.removeAttribute('hidden');
  scan();
}

/**
 * Handles tab show event - starts/stops scanning based on tab
 * @param {CustomEvent} evt - Tab show event
 */
export function handleTabShow(evt) {
  const tabId = evt.detail.tabId;
  const currentVideoCaptureEl = document.querySelector('video-capture');

  if (tabId === 'cameraTab') {
    startScanning();

    if (!currentVideoCaptureEl) {
      return;
    }

    if (!currentVideoCaptureEl.loading && scanBtn?.hasAttribute('hidden')) {
      scanFrameEl?.removeAttribute('hidden');
      videoCaptureActionsEl?.removeAttribute('hidden');
      scan();
    }

    if (typeof currentVideoCaptureEl.startVideoStream === 'function') {
      const videoDeviceId = cameraSelect?.value || undefined;
      currentVideoCaptureEl.startVideoStream(videoDeviceId);
    }
  } else if (tabId === 'fileTab') {
    stopScanning();

    if (currentVideoCaptureEl && typeof currentVideoCaptureEl.stopVideoStream === 'function') {
      currentVideoCaptureEl.stopVideoStream();
    }

    scanFrameEl?.setAttribute('hidden', '');
    videoCaptureActionsEl?.setAttribute('hidden', '');
  }
}

/**
 * Handles file selection for barcode scanning
 * @param {File} file - Selected file
 */
export async function handleFileSelect(file) {
  if (!file) return;

  const [, settings] = await getSettings();
  const image = new Image();
  const reader = new FileReader();

  reader.onload = evt => {
    const data = evt.target.result;

    image.onload = async () => {
      try {
        const barcodeReader = getBarcodeReader();
        if (!barcodeReader) {
          throw new Error('Scanner not initialized');
        }

        const barcode = await barcodeReader.detect(image);
        const barcodeValue = extractBarcodeValue(barcode);

        if (!barcodeValue) {
          throw new Error('No barcode detected');
        }

        log.info(`Barcode detected from image: ${barcodeValue}`);
        await fetchProductData(barcodeValue);

        if (settings?.addToHistory) {
          bsHistoryEl?.add(barcodeValue);
        }

        triggerScanEffects();
      } catch (err) {
        log.error(err);
        toastify(
          '<strong>No barcode detected</strong><br><small>Please try again with a different image.</small>',
          { variant: 'danger', trustDangerousInnerHTML: true }
        );
        triggerScanEffects({ success: false });
      }
    };

    image.src = data;
    image.alt = 'Image preview';

    // Update dropzone preview
    if (dropzoneEl) {
      dropzoneEl.replaceChildren();

      const preview = document.createElement('div');
      preview.className = 'dropzone-preview';

      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'dropzone-preview__image-wrapper';

      const fileNameWrapper = document.createElement('div');
      fileNameWrapper.className = 'dropzone-preview__file-name';
      fileNameWrapper.textContent = file.name;

      imageWrapper.appendChild(image);
      preview.appendChild(imageWrapper);
      preview.appendChild(fileNameWrapper);
      dropzoneEl.prepend(preview);
    }
  };

  reader.readAsDataURL(file);
}

/**
 * Handles file drop event
 * @param {CustomEvent} evt - Drop event
 */
export function handleFileDrop(evt) {
  const file = evt.detail.acceptedFiles[0];
  handleFileSelect(file);
}

/**
 * Handles video capture resize
 */
export function handleVideoCaptureResize() {
  const videoEl = videoCaptureEl?.shadowRoot?.querySelector('video');
  if (videoEl && scanFrameEl) {
    resizeScanFrame(videoEl, scanFrameEl);
  }
}

/**
 * Handles torch button click
 * @param {MouseEvent} evt - Click event
 */
export function handleTorchButtonClick(evt) {
  if (!videoCaptureEl) return;

  videoCaptureEl.torch = !videoCaptureEl.torch;

  toggleTorchButtonStatus({
    el: evt.currentTarget,
    isTorchOn: videoCaptureEl.hasAttribute('torch')
  });
}

/**
 * Handles camera select change
 * @param {Event} evt - Change event
 */
export function handleCameraSelectChange(evt) {
  if (typeof videoCaptureEl?.restartVideoStream !== 'function') {
    return;
  }

  const videoDeviceId = evt.target.value || undefined;
  videoCaptureEl.restartVideoStream(videoDeviceId);
}

/**
 * Handles document visibility change - pauses/resumes scanning
 * @param {string} selectedTabId - Currently selected tab ID
 */
export function handleDocumentVisibilityChange(selectedTabId) {
  if (selectedTabId !== 'cameraTab') {
    return;
  }

  const currentVideoCaptureEl = document.querySelector('video-capture');

  if (document.visibilityState === 'hidden') {
    stopScanning();

    if (currentVideoCaptureEl && typeof currentVideoCaptureEl.stopVideoStream === 'function') {
      currentVideoCaptureEl.stopVideoStream();
    }
  } else {
    startScanning();

    if (!currentVideoCaptureEl) {
      return;
    }

    if (!currentVideoCaptureEl.loading && scanBtn?.hasAttribute('hidden')) {
      scan();
    }

    if (typeof currentVideoCaptureEl.startVideoStream === 'function') {
      const videoDeviceId = cameraSelect?.value || undefined;
      currentVideoCaptureEl.startVideoStream(videoDeviceId);
    }
  }
}
